// ✅ src/components/screens/OrderDetailsScreen.js (FULL - SUPER MODERN LIGHT)
// Seller view: /seller/orders/:orderId
// ✅ Modern clean LIGHT UI (sticky header, clean cards, timeline, proof lightbox)
// ✅ NON-STRICT: works even if fields are missing or saved in different shapes
// ✅ Shows: buyer + address + delivery + payment + items + tracking history + images
// ✅ NEW: Debug panel + ultra-tolerant size/color extraction

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { auth, db } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

/* ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}
function upper(v) {
  return safe(v).toUpperCase();
}
function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function money(n, cur = "GHS") {
  const num = toNum(n, 0);
  return `${cur} ${num.toLocaleString()}`;
}
function fmtDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  } catch {
    return "—";
  }
}
function normalizePayLabel(type) {
  const t = upper(type);
  if (t === "MOMO" || t === "MOBILE_MONEY") return "MoMo";
  if (t === "BANK" || t === "BANK_TRANSFER") return "Bank Transfer";
  if (t === "CASH") return "Cash";
  return t || "—";
}
function normalizeDelLabel(type) {
  const t = upper(type);
  if (t.includes("PICK")) return "Local Pickup";
  if (t.includes("MEET")) return "Meet-up";
  if (t.includes("DELIV")) return "Delivery";
  return safe(type) || "Delivery";
}
function bestItemImage(it) {
  return (
    safe(it?.imageUrl) ||
    safe(it?.thumbnailUrl) ||
    safe(it?.image) ||
    safe(it?.photoUrl) ||
    safe(it?.thumb) ||
    ""
  );
}
function firstNonEmpty(...vals) {
  for (const v of vals) {
    const s = safe(v);
    if (s) return s;
  }
  return "";
}
function statusMeta(statusRaw) {
  const s = upper(statusRaw);

  const map = {
    NEW: { label: "NEW", tone: "info" },
    PENDING_CONFIRMATION: { label: "PENDING CONFIRMATION", tone: "warn" },
    CONFIRMED: { label: "CONFIRMED", tone: "good" },
    PACKED: { label: "PACKED", tone: "info" },
    OUT_FOR_DELIVERY: { label: "OUT FOR DELIVERY", tone: "info" },
    DELIVERED: { label: "DELIVERED", tone: "good" },
    COMPLETED: { label: "COMPLETED", tone: "good" },
    CANCELLED: { label: "CANCELLED", tone: "bad" },
    CANCELED: { label: "CANCELLED", tone: "bad" },
    FAILED: { label: "FAILED", tone: "bad" },
  };

  return map[s] || { label: safe(statusRaw) || "—", tone: "muted" };
}

function openExternal(url) {
  const u = safe(url);
  if (!u) return;
  window.open(u, "_blank", "noopener,noreferrer");
}

/* ---------------- UI helpers ---------------- */
function initials(name) {
  const s = safe(name);
  if (!s) return "U";
  const parts = s.split(" ").filter(Boolean);
  const a = parts[0]?.[0] || "U";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase();
}

/* ✅ NEW: option extractor (handles MANY shapes) */
function pickOption(it, key) {
  const k = String(key || "").toLowerCase();

  const direct =
    it?.[k] ??
    it?.[`selected${k[0]?.toUpperCase?.() + k.slice(1)}`] ??
    it?.[`chosen${k[0]?.toUpperCase?.() + k.slice(1)}`];

  const fromOptions =
    it?.options?.[k] ??
    it?.selectedOptions?.[k] ??
    it?.variant?.[k] ??
    it?.attributes?.[k] ??
    it?.meta?.[k];

  // sometimes saved like: attributes: { size: "30-50" } (your screenshot shows attributes.size)
  const s = safe(direct) || safe(fromOptions);
  return s;
}

/* ---------------- component ---------------- */
export default function OrderDetailsScreen() {
  const nav = useNavigate();
  const { orderId } = useParams();

  const [user, setUser] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);

  // lightbox
  const [lightboxUrl, setLightboxUrl] = useState("");
  const [lightboxTitle, setLightboxTitle] = useState("");

  // ✅ debug
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setMsg("Please login first.");
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user || !orderId) return;

    let alive = true;
    setLoading(true);
    setMsg("");

    const ref = doc(db, "orders", orderId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!alive) return;

        if (!snap.exists()) {
          setOrder(null);
          setMsg("Order not found.");
          setLoading(false);
          return;
        }

        const data = snap.data() || {};
        setOrder({ id: snap.id, ...data });
        setLoading(false);
      },
      (err) => {
        if (!alive) return;
        setMsg(err?.message || "Failed to load order.");
        setLoading(false);
      }
    );

    return () => {
      alive = false;
      try {
        unsub();
      } catch {}
    };
  }, [user, orderId]);

  // ✅ items (supports many shapes, INCLUDING itemsSnapshot)
  const items = useMemo(() => {
    // ✅ CURRENT CHECKOUT SAVES CART HERE
    const snap = order?.itemsSnapshot;
    if (Array.isArray(snap) && snap.length) return snap;

    // older / alternative shapes
    const a = order?.items;
    if (Array.isArray(a) && a.length) return a;

    const b = order?.cartItems;
    if (Array.isArray(b) && b.length) return b;

    const c = order?.products;
    if (Array.isArray(c) && c.length) return c;

    // single buy-now snapshot
    const ps = order?.productSnapshot;
    if (ps && typeof ps === "object") {
      return [
        {
          productId: ps.productId || ps.id,
          name: ps.title,
          title: ps.title,
          imageUrl: ps.thumbnailUrl,
          thumbnailUrl: ps.thumbnailUrl,
          unitPrice: ps.unitPrice,
          price: ps.unitPrice,
          qty: ps.qty,
          quantity: ps.qty,
          options: ps.options,
          size: ps.size,
          color: ps.color,
          selectedSize: ps.selectedSize,
          selectedColor: ps.selectedColor,
        },
      ];
    }

    return [];
  }, [order]);

  // tracking history
  const trackHistory = useMemo(() => {
    const h = order?.tracking?.history;
    if (!Array.isArray(h)) return [];
    const rows = [...h];
    rows.sort((x, y) => {
      const ax = x?.at?.toDate ? x.at.toDate().getTime() : new Date(x?.at || 0).getTime();
      const ay = y?.at?.toDate ? y.at.toDate().getTime() : new Date(y?.at || 0).getTime();
      return ay - ax; // newest first
    });
    return rows;
  }, [order]);

  // derived fields
  const currency = safe(order?.currency) || "GHS";
  const total = money(order?.total ?? order?.grandTotal ?? 0, currency);
  const subtotal = money(order?.subtotal ?? 0, currency);

  const status = safe(order?.status) || "—";
  const st = statusMeta(status);

  const createdAt = fmtDate(order?.createdAt);
  const updatedAt = fmtDate(order?.updatedAt);

  const buyerName =
    firstNonEmpty(order?.buyer?.name, order?.buyerName, order?.buyer?.fullName) || "Customer";
  const buyerPhone = firstNonEmpty(order?.buyer?.phone, order?.buyerPhone, order?.phone) || "—";
  const buyerEmail = firstNonEmpty(order?.buyer?.email, order?.buyerEmail) || "—";
  const buyerNote = firstNonEmpty(order?.buyer?.note, order?.note) || "";

  const payType = normalizePayLabel(order?.payment?.type);
  const payLabel = firstNonEmpty(order?.payment?.label, payType) || "—";
  const payRef = firstNonEmpty(order?.payment?.reference, order?.paymentReference) || "";
  const proofUrl = firstNonEmpty(order?.payment?.proofUrl, order?.proofUrl) || "";

  const paidByBuyer =
    order?.payment?.paidByBuyer === true ||
    upper(order?.status) === "PENDING_CONFIRMATION" ||
    upper(order?.payment?.status) === "PENDING_CONFIRMATION" ||
    upper(order?.payment?.status) === "PAID";

  const delType = normalizeDelLabel(order?.delivery?.type || order?.deliveryType || "DELIVERY");

  const addr = order?.address || order?.deliveryAddress || order?.shippingAddress || {};
  const addrLabel = firstNonEmpty(addr?.label, addr?.title, addr?.name) || "";
  const addrLine1 = firstNonEmpty(addr?.addressLine1, addr?.line1, addr?.address) || "";
  const addrLine2 = firstNonEmpty(addr?.addressLine2, addr?.line2) || "";
  const addrCity = firstNonEmpty(addr?.city) || "";
  const addrRegion = firstNonEmpty(addr?.region, addr?.state, addr?.province) || "";
  const addrLandmark = firstNonEmpty(addr?.landmark) || "";
  const addrPhone = firstNonEmpty(addr?.phone) || "";
  const addrFullName = firstNonEmpty(addr?.fullName, addr?.name) || "";

  const delTitle = firstNonEmpty(order?.delivery?.title, order?.delivery?.etaText, "") || "";
  const delFeeNum =
    upper(order?.delivery?.pricingMode) === "QUOTE" ? null : toNum(order?.delivery?.fee, 0);
  const delFeeText = delFeeNum == null ? "QUOTE (decided later)" : money(delFeeNum, currency);

  const storeName =
    firstNonEmpty(order?.storeName, order?.store?.name, order?.store?.storeName) || "Store";

  const headerSubtitle = useMemo(() => {
    const parts = [];
    if (safe(storeName)) parts.push(storeName);
    if (createdAt !== "—") parts.push(`Created: ${createdAt}`);
    return parts.join(" • ");
  }, [storeName, createdAt]);

  const computedItemsTotal = useMemo(() => {
    try {
      return (items || []).reduce((sum, it) => {
        const qty = Math.max(1, toNum(it?.qty ?? it?.quantity ?? 1, 1));
        const unit = toNum(it?.unitPrice ?? it?.price ?? it?.amount ?? 0, 0);
        return sum + qty * unit;
      }, 0);
    } catch {
      return 0;
    }
  }, [items]);

  const showComputedHint = useMemo(() => {
    const t = toNum(order?.total, NaN);
    return !Number.isFinite(t) && computedItemsTotal > 0;
  }, [order?.total, computedItemsTotal]);

  const actions = useMemo(() => {
    const phone = safe(buyerPhone).replace(/\s+/g, "");
    const email = safe(buyerEmail);
    const hasPhone = phone && phone !== "—";
    const hasEmail = email && email !== "—";
    const whatsapp = hasPhone ? `https://wa.me/${phone.replace(/^\+/, "")}` : "";
    return { hasPhone, hasEmail, phone, email, whatsapp };
  }, [buyerPhone, buyerEmail]);

  const openProof = () => {
    if (!proofUrl) return;
    setLightboxUrl(proofUrl);
    setLightboxTitle("Payment proof");
  };

  const openItemImage = (url, name) => {
    const u = safe(url);
    if (!u) return;
    setLightboxUrl(u);
    setLightboxTitle(safe(name) || "Image");
  };

  const closeLightbox = () => {
    setLightboxUrl("");
    setLightboxTitle("");
  };

  useEffect(() => {
    if (!lightboxUrl) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxUrl]);

  // ✅ debug payload
  const debugSample = useMemo(() => {
    const first = items?.[0] || null;
    const size = first ? pickOption(first, "size") : "";
    const color = first ? pickOption(first, "color") : "";
    return {
      itemsLen: Array.isArray(items) ? items.length : 0,
      firstItem: first,
      picked: { size, color },
      note:
        !size && !color
          ? "No size/color found in order itemsSnapshot. This means Checkout did not save it."
          : "Size/color exists in order itemsSnapshot ✅",
    };
  }, [items]);

  return (
    <div className="odx">
      <style>{css}</style>

      {/* lightbox */}
      {lightboxUrl ? (
        <div className="lb" role="dialog" aria-modal="true">
          <button className="lb-bg" type="button" onClick={closeLightbox} aria-label="Close" />
          <div className="lb-card">
            <div className="lb-top">
              <div className="lb-title">{lightboxTitle}</div>
              <div className="lb-actions">
                <button className="btn ghost" type="button" onClick={() => openExternal(lightboxUrl)}>
                  Open
                </button>
                <button className="btn" type="button" onClick={closeLightbox}>
                  Done
                </button>
              </div>
            </div>
            <div className="lb-imgWrap">
              <img src={lightboxUrl} alt={lightboxTitle || "preview"} />
            </div>
          </div>
        </div>
      ) : null}

      <div className="wrap">
        {/* top bar */}
        <div className="top">
          <button className="btn ghost" onClick={() => nav(-1)}>
            ← Back
          </button>

          <div className="topMid">
            <div className="h1">Order Details</div>
            <div className="sub">{headerSubtitle}</div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button className="btn ghost" type="button" onClick={() => setShowDebug((s) => !s)}>
              {showDebug ? "Hide Debug" : "Debug"}
            </button>

            <div className={`pill ${st.tone}`}>
              <span className="dot" />
              <span>{st.label}</span>
            </div>
          </div>
        </div>

        {showDebug ? (
          <div className="debugCard">
            <div className="debugTitle">DEBUG (Order items snapshot)</div>
            <pre className="debugPre">{JSON.stringify(debugSample, null, 2)}</pre>
          </div>
        ) : null}

        {msg ? <div className={`notice ${msg.includes("✅") ? "" : "err"}`}>{msg}</div> : null}

        {loading ? (
          <div className="card">Loading…</div>
        ) : !order ? (
          <div className="card">No order.</div>
        ) : (
          <div className="grid">
            {/* left column */}
            <div className="col">
              <div className="card">
                <div className="cardHead">
                  <div>
                    <div className="cardTitle">Overview</div>
                    <div className="mini">Order ID: {order.id}</div>
                  </div>
                  <div className="mini">{safe(order?.orderNumber) ? `#${safe(order?.orderNumber)}` : ""}</div>
                </div>

                <div className="kv">
                  <div className="kvRow">
                    <div className="k">Created</div>
                    <div className="v">{createdAt}</div>
                  </div>
                  <div className="kvRow">
                    <div className="k">Updated</div>
                    <div className="v">{updatedAt}</div>
                  </div>

                  <div className="kvRow">
                    <div className="k">Subtotal</div>
                    <div className="v">{subtotal}</div>
                  </div>
                  <div className="kvRow">
                    <div className="k">Delivery fee</div>
                    <div className="v">{delFeeText}</div>
                  </div>
                  <div className="kvRow strong">
                    <div className="k">Total</div>
                    <div className="v">{total}</div>
                  </div>

                  {showComputedHint ? (
                    <div className="hint">
                      Total was missing on this order doc, so items compute to{" "}
                      <b>{money(computedItemsTotal, currency)}</b>.
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="card">
                <div className="cardHead">
                  <div>
                    <div className="cardTitle">Buyer</div>
                    <div className="mini">Contact + notes</div>
                  </div>
                </div>

                <div className="buyer">
                  <div className="buyerMain">
                    <div className="avatar">{initials(buyerName)}</div>
                    <div className="buyerText">
                      <div className="buyerName">{buyerName}</div>
                      <div className="buyerSub">
                        <span>{buyerPhone}</span>
                        <span className="sep">•</span>
                        <span>{buyerEmail}</span>
                      </div>
                    </div>
                  </div>

                  <div className="btnRow">
                    <button
                      className="btn"
                      type="button"
                      disabled={!actions.hasPhone}
                      onClick={() => openExternal(`tel:${actions.phone}`)}
                    >
                      Call
                    </button>
                    <button
                      className="btn"
                      type="button"
                      disabled={!actions.whatsapp}
                      onClick={() => openExternal(actions.whatsapp)}
                    >
                      WhatsApp
                    </button>
                    <button
                      className="btn"
                      type="button"
                      disabled={!actions.hasEmail}
                      onClick={() => openExternal(`mailto:${actions.email}`)}
                    >
                      Email
                    </button>
                  </div>

                  {buyerNote ? <div className="noteBox">{buyerNote}</div> : null}
                </div>
              </div>

              <div className="card">
                <div className="cardHead">
                  <div>
                    <div className="cardTitle">Delivery</div>
                    <div className="mini">{delType}</div>
                  </div>
                </div>

                <div className="address">
                  <div className="addrLine">
                    <div className="addrK">Option</div>
                    <div className="addrV">{firstNonEmpty(order?.delivery?.title, delType) || "—"}</div>
                  </div>

                  {delTitle ? (
                    <div className="addrLine">
                      <div className="addrK">Info</div>
                      <div className="addrV">{delTitle}</div>
                    </div>
                  ) : null}

                  <div className="addrCard">
                    <div className="addrTop">
                      <div className="addrTitle">{addrLabel || "Delivery Address"}</div>
                      <div className="addrMini">
                        {addrCity || addrRegion ? `${addrCity}${addrCity && addrRegion ? ", " : ""}${addrRegion}` : ""}
                      </div>
                    </div>

                    <div className="addrBody">
                      {addrFullName ? (
                        <div className="addrText">
                          <b>Receiver:</b> {addrFullName}
                        </div>
                      ) : null}
                      {addrPhone ? (
                        <div className="addrText">
                          <b>Phone:</b> {addrPhone}
                        </div>
                      ) : null}
                      {addrLine1 ? <div className="addrText">{addrLine1}</div> : null}
                      {addrLine2 ? <div className="addrText">{addrLine2}</div> : null}
                      {addrLandmark ? <div className="addrText muted">Landmark: {addrLandmark}</div> : null}

                      {!addrLine1 && !addrLine2 && !addrCity && !addrRegion && !addrLandmark ? (
                        <div className="addrText muted">—</div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* right column */}
            <div className="col">
              <div className="card">
                <div className="cardHead">
                  <div>
                    <div className="cardTitle">Payment</div>
                    <div className="mini">{payLabel}</div>
                  </div>
                </div>

                <div className="kv">
                  <div className="kvRow">
                    <div className="k">Type</div>
                    <div className="v">{payLabel}</div>
                  </div>
                  <div className="kvRow">
                    <div className="k">Reference</div>
                    <div className="v">{payRef || "—"}</div>
                  </div>
                  <div className="kvRow">
                    <div className="k">Buyer Paid</div>
                    <div className="v">{paidByBuyer ? "Yes" : "No"}</div>
                  </div>

                  <div className="proof">
                    <div className="k">Proof</div>
                    <div className="proofRight">
                      {proofUrl ? (
                        <>
                          <button className="btn" type="button" onClick={openProof}>
                            View proof
                          </button>
                          <button className="thumbBtn" type="button" onClick={openProof} title="Open proof">
                            <img src={proofUrl} alt="proof" />
                          </button>
                        </>
                      ) : (
                        <div className="muted">—</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="cardHead">
                  <div>
                    <div className="cardTitle">Items</div>
                    <div className="mini">{items.length} item(s)</div>
                  </div>
                </div>

                {items.length === 0 ? (
                  <div className="muted">No items saved on this order.</div>
                ) : (
                  <div className="items">
                    {items.map((it, idx) => {
                      const name = firstNonEmpty(it?.name, it?.title) || `Item ${idx + 1}`;
                      const qty = Math.max(1, toNum(it?.qty ?? it?.quantity ?? 1, 1));
                      const unit = toNum(it?.unitPrice ?? it?.price ?? it?.amount ?? 0, 0);
                      const line = unit * qty;

                      const img = bestItemImage(it);

                      // ✅ SUPER TOLERANT: reads from options/attributes/top-level
                      const color = pickOption(it, "color");
                      const size = pickOption(it, "size");

                      return (
                        <div className="item" key={`${order.id}-it-${idx}`}>
                          <button
                            className="itemImg"
                            type="button"
                            onClick={() => (img ? openItemImage(img, name) : null)}
                            title={img ? "Open image" : ""}
                          >
                            {img ? <img src={img} alt={name} /> : <div className="ph">🛍️</div>}
                          </button>

                          <div className="itemMid">
                            <div className="itemName">{name}</div>
                            <div className="itemMeta">
                              <span>
                                Qty: <b>{qty}</b>
                              </span>

                              {color ? (
                                <>
                                  <span className="sep">•</span>
                                  <span>
                                    Color: <b>{color}</b>
                                  </span>
                                </>
                              ) : null}

                              {size ? (
                                <>
                                  <span className="sep">•</span>
                                  <span>
                                    Size: <b>{size}</b>
                                  </span>
                                </>
                              ) : null}

                              {!color && !size ? (
                                <>
                                  <span className="sep">•</span>
                                  <span className="muted">(no options saved)</span>
                                </>
                              ) : null}
                            </div>
                          </div>

                          <div className="itemRight">
                            <div className="money">{money(line, currency)}</div>
                            <div className="mini muted">{money(unit, currency)} each</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="card">
                <div className="cardHead">
                  <div>
                    <div className="cardTitle">Tracking</div>
                    <div className="mini">Current: {safe(order?.tracking?.currentStep) || "—"}</div>
                  </div>
                </div>

                {trackHistory.length === 0 ? (
                  <div className="muted">No tracking history yet.</div>
                ) : (
                  <div className="timeline">
                    {trackHistory.map((h, i) => (
                      <div className="tRow" key={`${order.id}-h-${i}`}>
                        <div className="tDot" />
                        <div className="tBody">
                          <div className="tTop">
                            <div className="tStep">{safe(h?.step) || "—"}</div>
                            <div className="tTime">{fmtDate(h?.at)}</div>
                          </div>
                          <div className="tText">{safe(h?.text) || "—"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="footSpace" />
      </div>
    </div>
  );
}

/* ---------------- CSS (LIGHT PREMIUM) ---------------- */
const css = `
:root{
  --bg1:#f6f7fb;
  --bg2:#ffffff;
  --card:#ffffff;
  --line:rgba(15,23,42,.10);
  --text:#0f172a;
  --muted:rgba(15,23,42,.62);
  --shadow: 0 10px 30px rgba(15,23,42,.08);
  --shadow2: 0 8px 18px rgba(15,23,42,.06);

  --good:#16a34a;
  --warn:#f59e0b;
  --bad:#ef4444;
  --info:#2563eb;

  --softGood: rgba(22,163,74,.10);
  --softWarn: rgba(245,158,11,.12);
  --softBad: rgba(239,68,68,.10);
  --softInfo: rgba(37,99,235,.10);
}

*{box-sizing:border-box}
.odx{
  min-height:100vh;
  background:
    radial-gradient(900px 420px at 10% -10%, rgba(99,102,241,.18), transparent 60%),
    radial-gradient(900px 420px at 95% 0%, rgba(34,197,94,.12), transparent 55%),
    radial-gradient(900px 420px at 60% 110%, rgba(244,63,94,.10), transparent 55%),
    linear-gradient(180deg, var(--bg1), var(--bg2));
  color:var(--text);
  font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
  padding: 18px 14px;
}

.wrap{max-width:1100px;margin:0 auto}

.top{
  position: sticky;
  top: 10px;
  z-index: 5;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding: 12px;
  border-radius: 18px;
  border:1px solid var(--line);
  background: rgba(255,255,255,.75);
  backdrop-filter: blur(14px);
  box-shadow: var(--shadow2);
}

.topMid{min-width:0;flex:1;display:flex;flex-direction:column;gap:4px}
.h1{font-size:18px;font-weight:900;letter-spacing:-.01em}
.sub{color:var(--muted);font-weight:700;font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}

.btn{
  border:1px solid var(--line);
  background: #fff;
  color: var(--text);
  border-radius: 12px;
  padding: 10px 12px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 6px 14px rgba(15,23,42,.05);
  transition: transform .08s ease, box-shadow .2s ease, background .2s ease;
}
.btn:hover{transform: translateY(-1px); box-shadow: 0 10px 20px rgba(15,23,42,.08)}
.btn:disabled{opacity:.55;cursor:not-allowed;transform:none}
.btn.ghost{background: transparent; box-shadow: none;}

.pill{
  display:flex;
  align-items:center;
  gap:10px;
  border-radius: 999px;
  padding: 10px 12px;
  border:1px solid var(--line);
  background: #fff;
  font-weight: 900;
  font-size: 12px;
  white-space:nowrap;
}
.pill .dot{width:10px;height:10px;border-radius:999px;background: rgba(15,23,42,.28);}
.pill.good{border-color: rgba(22,163,74,.22); background: var(--softGood)}
.pill.good .dot{background: rgba(22,163,74,.95)}
.pill.warn{border-color: rgba(245,158,11,.25); background: var(--softWarn)}
.pill.warn .dot{background: rgba(245,158,11,.95)}
.pill.bad{border-color: rgba(239,68,68,.22); background: var(--softBad)}
.pill.bad .dot{background: rgba(239,68,68,.95)}
.pill.info{border-color: rgba(37,99,235,.22); background: var(--softInfo)}
.pill.info .dot{background: rgba(37,99,235,.95)}
.pill.muted{opacity:.9}

.notice{
  margin-top: 12px;
  border-radius: 16px;
  border:1px solid var(--line);
  background: rgba(255,255,255,.85);
  padding: 12px;
  font-weight: 800;
  box-shadow: var(--shadow2);
}
.notice.err{border-color: rgba(239,68,68,.22); background: rgba(239,68,68,.08);}

.grid{
  margin-top: 12px;
  display:grid;
  grid-template-columns: 1.05fr .95fr;
  gap: 12px;
  align-items:start;
}
@media (max-width: 980px){ .grid{grid-template-columns: 1fr} }

.col{display:grid;gap:12px}

.card{
  border-radius: 20px;
  border:1px solid var(--line);
  background: rgba(255,255,255,.92);
  box-shadow: var(--shadow);
  padding: 14px;
}

.cardHead{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:10px;
  margin-bottom: 10px;
}
.cardTitle{font-weight:900;letter-spacing:-.01em}
.mini{color:var(--muted);font-weight:700;font-size:12px}

.kv{display:grid;gap:10px}
.kvRow{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  border:1px solid var(--line);
  background: rgba(15,23,42,.03);
  border-radius: 14px;
  padding: 10px 12px;
}
.k{color:var(--muted);font-weight:800;font-size:12px}
.v{font-weight:900;text-align:right;word-break:break-word}
.kvRow.strong{background: rgba(37,99,235,.06); border-color: rgba(37,99,235,.18);}
.hint{
  margin-top: 8px;
  border:1px solid rgba(37,99,235,.18);
  background: rgba(37,99,235,.06);
  padding: 10px 12px;
  border-radius: 14px;
  color: rgba(15,23,42,.86);
  font-weight: 750;
  font-size: 12px;
  line-height: 1.35;
}

.buyer{display:grid;gap:12px}
.buyerMain{display:flex;align-items:center;gap:12px}
.avatar{
  width:44px;height:44px;border-radius:16px;
  border:1px solid var(--line);
  background: rgba(37,99,235,.10);
  display:grid;place-items:center;
  font-weight:900;
}
.buyerName{font-weight:900}
.buyerSub{color:var(--muted);font-weight:700;font-size:12px;display:flex;gap:8px;flex-wrap:wrap}
.sep{opacity:.55}
.btnRow{display:flex;gap:10px;flex-wrap:wrap}
.noteBox{
  border:1px solid var(--line);
  background: rgba(15,23,42,.03);
  border-radius: 14px;
  padding: 12px;
  font-weight: 700;
  color: rgba(15,23,42,.88);
  line-height:1.35;
}

.address{display:grid;gap:10px}
.addrLine{
  display:flex;justify-content:space-between;gap:10px;
  border:1px solid var(--line);
  background: rgba(15,23,42,.03);
  border-radius: 14px;
  padding: 10px 12px;
}
.addrK{color:var(--muted);font-weight:800;font-size:12px}
.addrV{font-weight:900;text-align:right;word-break:break-word}
.addrCard{
  border:1px solid var(--line);
  background: rgba(255,255,255,.95);
  border-radius: 16px;
  overflow:hidden;
}
.addrTop{
  padding: 12px;
  border-bottom:1px solid var(--line);
  background: rgba(15,23,42,.02);
}
.addrTitle{font-weight:900}
.addrMini{margin-top:3px;color:var(--muted);font-weight:700;font-size:12px}
.addrBody{padding:12px;display:grid;gap:6px}
.addrText{font-weight:700}
.muted{color:var(--muted);font-weight:700}

.proof{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  border:1px solid var(--line);
  background: rgba(15,23,42,.03);
  border-radius: 14px;
  padding: 10px 12px;
}
.proofRight{display:flex;align-items:center;gap:10px;justify-content:flex-end;flex-wrap:wrap}
.thumbBtn{
  border:1px solid var(--line);
  background: #fff;
  border-radius: 14px;
  padding: 0;
  width: 56px;
  height: 56px;
  overflow:hidden;
  cursor:pointer;
  box-shadow: 0 8px 18px rgba(15,23,42,.06);
}
.thumbBtn img{width:100%;height:100%;object-fit:cover;display:block}

.items{display:grid;gap:10px}
.item{
  display:grid;
  grid-template-columns: 70px 1fr auto;
  gap: 12px;
  align-items:center;
  border:1px solid var(--line);
  background: rgba(15,23,42,.02);
  border-radius: 16px;
  padding: 10px;
}
@media (max-width: 520px){
  .item{grid-template-columns: 64px 1fr; grid-template-areas: "img mid" "img right"}
  .itemRight{grid-area:right; justify-self:end}
}
.itemImg{
  width:70px;height:70px;border-radius:16px;overflow:hidden;
  border:1px solid var(--line);
  background: #fff;
  padding:0;
  cursor:pointer;
  display:grid;place-items:center;
  box-shadow: 0 8px 18px rgba(15,23,42,.05);
}
.itemImg img{width:100%;height:100%;object-fit:cover;display:block}
.ph{opacity:.9}
.itemName{font-weight:900;line-height:1.15}
.itemMeta{margin-top:6px;color:var(--muted);font-weight:700;font-size:12px;display:flex;gap:8px;flex-wrap:wrap}
.itemRight{text-align:right}
.money{font-weight:900}
.itemRight .mini{margin-top:4px}

.timeline{display:grid;gap:10px}
.tRow{
  display:grid;
  grid-template-columns: 14px 1fr;
  gap: 10px;
  align-items:start;
}
.tDot{
  width:12px;height:12px;border-radius:999px;
  background: rgba(37,99,235,.95);
  box-shadow: 0 0 0 6px rgba(37,99,235,.14);
  margin-top: 6px;
}
.tBody{
  border:1px solid var(--line);
  background: rgba(255,255,255,.95);
  border-radius: 16px;
  padding: 10px 12px;
}
.tTop{display:flex;justify-content:space-between;gap:10px;align-items:center}
.tStep{font-weight:900}
.tTime{color:var(--muted);font-weight:700;font-size:12px;text-align:right}
.tText{margin-top:6px;font-weight:700;color: rgba(15,23,42,.88)}

.footSpace{height:18px}

/* ✅ debug panel */
.debugCard{
  margin-top: 12px;
  border-radius: 16px;
  border:1px solid rgba(37,99,235,.22);
  background: rgba(37,99,235,.06);
  padding: 12px;
  box-shadow: var(--shadow2);
}
.debugTitle{font-weight:900;margin-bottom:8px}
.debugPre{
  margin:0;
  font-size:12px;
  line-height:1.35;
  white-space:pre-wrap;
  word-break:break-word;
}

/* Lightbox */
.lb{
  position:fixed;
  inset:0;
  z-index:9999;
  display:grid;
  place-items:center;
  padding: 18px;
}
.lb-bg{
  position:absolute;
  inset:0;
  border:0;
  background: rgba(2,6,23,.55);
  cursor:pointer;
}
.lb-card{
  position:relative;
  width:min(980px, 96vw);
  max-height: 86vh;
  border-radius: 20px;
  border:1px solid var(--line);
  background: rgba(255,255,255,.96);
  backdrop-filter: blur(10px);
  box-shadow: 0 18px 60px rgba(2,6,23,.25);
  overflow:hidden;
  display:flex;
  flex-direction:column;
}
.lb-top{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  padding: 12px;
  border-bottom:1px solid var(--line);
  background: rgba(15,23,42,.02);
}
.lb-title{font-weight:900}
.lb-actions{display:flex;gap:10px;flex-wrap:wrap}
.lb-imgWrap{padding: 12px; overflow:auto}
.lb-imgWrap img{
  width:100%;
  max-height: 72vh;
  object-fit: contain;
  display:block;
  border-radius: 14px;
  border:1px solid var(--line);
  background: #fff;
}
`;
